import React, { useCallback, useEffect } from 'react';
import Tabs from '@material-ui/core/Tabs';
import { TabPanel } from '../TabPanel/TabPanel.component';
import { useStyles } from './VerticalTabs.styles';
import { TabItem } from './VerticalTabs.types';
import { StyledTab } from '../Tab/Tab.component';
import { CSSProperties } from 'styled-components';
import { Collapse } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { useTranslation } from 'react-i18next';

interface VerticalTabsProps {
  items: TabItem[];
  style?: CSSProperties;
  selectedTab?: number;
  selectedTabCollapsed?: TabPanelCollapsedValue;
  tabPanelHeader?: React.ReactNode;
  tabPanelSubHeader?: React.ReactNode;
  onChange?: (tabId: number) => void;
  reloadActiveDialogContent?: (label: string) => void;
}

interface TabPanelCollapsed {
  labelName: string | undefined;
  isOpen: boolean;
}

export interface TabPanelCollapsedValue {
  labelName: string | undefined;
  index: number | boolean;
}

export const VerticalTabs: React.FC<VerticalTabsProps> = ({
  items,
  style,
  selectedTab,
  selectedTabCollapsed,
  tabPanelHeader,
  tabPanelSubHeader,
  onChange,
  reloadActiveDialogContent,
}) => {
  const classes = useStyles();
  const collapsingItems = items.filter((item) => item.collapsable);

  const [value, setValue] = React.useState<number>(selectedTab ?? 0);
  const [collapsingValue, setCollapsingValue] = React.useState<TabPanelCollapsedValue[]>(
    selectedTabCollapsed
      ? collapsingItems.map((item) =>
          item.label === selectedTabCollapsed.labelName
            ? { labelName: item.label, index: 0 }
            : { labelName: item.label, index: false },
        )
      : collapsingItems.map((item) => ({ labelName: item.label, index: false })),
  );
  const [open, setOpen] = React.useState<TabPanelCollapsed[]>(
    collapsingItems.map((item) => ({ labelName: item.label, isOpen: true })),
  );

  const { t } = useTranslation();

  const tabLabels = {
    controlTasksDistribution: t('sessionsParameters:tabLabels.controlTasksDistribution'),
    examinerBlockingLimits: t('sessionsParameters:tabLabels.examinerBlockingLimits'),
  };
  const handleChange = useCallback(
    (event: React.ChangeEvent<Record<string, never>>, newValue: number) => {
      setCollapsingValue(collapsingItems.map((item) => ({ labelName: item.label, index: false })));
      setValue(newValue);
      if (onChange) {
        onChange(newValue);
      }
      const activeTab = items[newValue]?.label;
      if (activeTab) {
        reloadActiveDialogContent && reloadActiveDialogContent(activeTab);
      }
    },
    [setCollapsingValue, collapsingItems, onChange, setValue, value],
  );

  const handleChangeCollapsingMenu = useCallback(
    (event: React.ChangeEvent<Record<string, never>>, newValue: number, newItem?: TabItem) => {
      const buttonLabel = event.target?.textContent; // needs to be textContent (not innerText) in order to let differenciate labels with white-spaces
      const convertedValue = newItem?.label
        ? items.findIndex((item) => item.label === buttonLabel && item.parentLabel === newItem.label)
        : items.findIndex((item) => item.label === buttonLabel);

      setCollapsingValue((prev) => {
        const newValues = prev.map((item) => {
          item.index = item.labelName === event.currentTarget?.name ? newValue : false;
          return item;
        });
        return newValues;
      });
      setValue(convertedValue);
      const activeTab = items[convertedValue]?.label;
      if (activeTab) {
        reloadActiveDialogContent && reloadActiveDialogContent(activeTab);
      }

      if (onChange) {
        setCollapsingValue((prev) => {
          const newValues = prev.map((item) => {
            item.index = item.labelName === event.currentTarget?.name ? newValue : false;
            return item;
          });
          return newValues;
        });
        setValue(convertedValue);
      }
    },
    [value, setValue, collapsingValue, setCollapsingValue, items, onChange],
  );

  const a11yProps = (index: number) => {
    return {
      id: `vertical-tab-${index}`,
      'aria-controls': `vertical-tabpanel-${index}`,
    };
  };

  const handleClick = useCallback(
    (e: React.SyntheticEvent<HTMLElement>) => {
      if (e.currentTarget && e.currentTarget?.innerText) {
        const innerText = e.currentTarget.innerText.trim();
        setOpen((prev) => {
          const value = prev.find((item) => item.labelName === innerText);
          if (value) {
            const newArray = prev.filter((item) => item.labelName !== innerText);
            value.isOpen = !value.isOpen;
            return [...newArray, value];
          } else return [...prev];
        });
      }
    },
    [setOpen, open],
  );

  const isOpen = (labelName?: string) => {
    if (labelName !== undefined) {
      const value = open.find((item) => item.labelName === labelName);
      return value ? value.isOpen : false;
    } else {
      return false;
    }
  };

  return (
    <div className={classes.root} style={{ overflowX: 'auto' }}>
      <Tabs
        orientation="vertical"
        value={value >= 0 ? value : false}
        onChange={handleChange}
        aria-label="Pionowe zakładki"
        classes={{
          root: classes.tabs,
          indicator: classes.indicator,
        }}
        style={style ?? undefined}
      >
        {items.map((item, index) => {
          return !item.collapsable && !item.parentLabel
            ? [
                <StyledTab
                  tabIndex={0}
                  style={{
                    display: !item.label ? 'none' : 'undefined',
                  }}
                  key={`tab-${item.label}-${index}`}
                  label={item.getInteractiveLabel ? item.getInteractiveLabel() : item.label ?? 'invisible tab'}
                  {...a11yProps(index)}
                />,
              ]
            : !item.parentLabel
            ? [
                <div
                  key={`collapsable-menu-${item.label}-${index}`}
                  style={{
                    flexGrow: 2,
                    alignContent: 'flex-start',
                    height: `${
                      (isOpen(item.label) ? items.filter((e) => e.parentLabel === item.label).length * 60 : 40) + 7
                    }px`,
                    width: 210,
                  }}
                >
                  <StyledTab
                    {...a11yProps(index)}
                    onClick={handleClick}
                    tabIndex={0}
                    label={
                      <span style={{ textAlign: 'center', justifyContent: 'center' }}>
                        {isOpen(item.label) ? (
                          <ArrowDropDownIcon style={{ marginBottom: -8, marginLeft: -6, marginRight: 2 }} />
                        ) : (
                          <ArrowDropUpIcon style={{ marginBottom: -8, marginLeft: -6, marginRight: 2 }} />
                        )}
                        {item.label}
                      </span>
                    }
                  />
                  <Collapse in={isOpen(item.label)} timeout={0}>
                    <Tabs
                      disableRipple
                      orientation="vertical"
                      value={
                        collapsingValue.find((e) => e.labelName === item.label)?.index !== undefined
                          ? collapsingValue.find((e) => e.labelName === item.label)!.index
                          : false
                      }
                      aria-label="Pionowe zakładki"
                      classes={{
                        root: classes.tabs,
                        indicator: classes.indicator,
                      }}
                      style={style ?? undefined}
                    >
                      {item.collapsedListItems &&
                        item.collapsedListItems.map((collapsedItem, index) => {
                          {
                            return (
                              <StyledTab
                                tabIndex={0}
                                disableRipple
                                name={item.label}
                                style={{ paddingLeft: 20, marginTop: -4 }}
                                key={`list-${collapsedItem}-${index}`}
                                sx={{ pl: 4 }}
                                onClick={(e: React.ChangeEvent<Record<string, never>>) =>
                                  handleChangeCollapsingMenu(e, index, item)
                                }
                                label={collapsedItem}
                                tooltipTitle={
                                  collapsedItem === tabLabels.controlTasksDistribution
                                    ? 'Przydzielanie zadań kontrolnych'
                                    : collapsedItem === tabLabels.examinerBlockingLimits
                                    ? 'Limity blokujące egzaminatora'
                                    : ''
                                }
                              />
                            );
                          }
                        })}
                    </Tabs>
                  </Collapse>
                </div>,
              ]
            : [
                <StyledTab
                  style={{
                    display: 'none',
                  }}
                  key={`tab-${item.label}-${index}`}
                  label={'invisible tab'}
                  {...a11yProps(index)}
                />,
              ];
        })}
      </Tabs>
      <div style={{ width: '100%' }}>
        {tabPanelHeader ?? null}
        {tabPanelSubHeader ?? null}
        {items.map((item, index) => (
          <TabPanel key={`tab-panel-${item.label}-${index}`} value={value} index={index}>
            {item.children}
          </TabPanel>
        ))}
      </div>
    </div>
  );
};
